import { Space } from "antd"
import { PageHeader } from "src/global/ui/PageHeader"
import { ErrorState } from "src/global/ui/error"
import { pluralize } from "src/global/utils/strings.ts"
import { MainContent, MainContentBlock } from "src/global/layout/MainContent.tsx"
import { DataAssetsEmptyScreen } from "src/pages/DataAssets/components/DataAssetsEmptyScreen.tsx"
import { ConnectToDataButton } from "src/pages/DataAssets/components/ConnectToDataButton.tsx"
import { useRequireRole } from "src/global/hooks/useRequireRole.ts"
import { ManageDatasourcesButton } from "src/pages/DataAssets/drawers/ManageDataSources/ManageDatasourcesButton.tsx"
import { AggregateAssetCoverageStats } from "src/pages/DataAssets/components/AggregateAssetCoverageStats"
import { useQuery } from "@apollo/client"
import { graphql } from "src/api/graphql/gql.ts"
import { exists } from "src/global/utils/typeguards"
import { DataAssetsTable } from "src/pages/DataAssets/components/DataAssetsTable"
import { useEventAction } from "src/global/hooks/useEventAction"
import { EventTypes } from "src/types/events"
import { useRealTimeUpdateMessage } from "src/global/hooks/useRealTimeUpdateMessage"
import { useIsFeatureEnabled } from "src/global/hooks/useIsFeatureEnabled.ts"

export const DataAssetTableDocument = graphql(`
  query DataAssetTableDocument {
    datasourcesV2 {
      id
      type
      name
      assets {
        id
        aggregateSuccessStatus
        lastValidatedAt
        name
        coverageStats {
          hasCompletenessCoverage
          hasSchemaCoverage
          hasVolumeCoverage
        }
      }
    }
  }
`)

export const DataAssets = () => {
  const isEditorOrAdmin = useRequireRole("EDITOR")
  const isDataHealthDashboardEnabled = useIsFeatureEnabled("dataHealthDashboardEnabled")
  const { data, loading, error, refetch } = useQuery(DataAssetTableDocument)
  const { show: showAssetChangedMessage, container: assetChangedMessage } = useRealTimeUpdateMessage({
    messageContent: "One of your Data Assets has changed",
    onRefresh: refetch,
  })

  useEventAction(EventTypes.DATA_ASSET.CREATED, () => showAssetChangedMessage(), { bufferDelay: 2000 })
  useEventAction(EventTypes.DATA_ASSET.UPDATED, () => showAssetChangedMessage(), { bufferDelay: 2000 })
  useEventAction(EventTypes.DATA_ASSET.DELETED, () => showAssetChangedMessage(), { bufferDelay: 2000 })

  const assetData = data?.datasourcesV2.flatMap((dataSource) =>
    dataSource.assets.filter(exists).map(({ id, name, lastValidatedAt, aggregateSuccessStatus, coverageStats }) => ({
      id,
      type: dataSource.type,
      dataSourceName: dataSource.name,
      name: name,
      lastValidation: lastValidatedAt ? lastValidatedAt : null,
      success: aggregateSuccessStatus ?? null,
      schema: coverageStats?.hasSchemaCoverage ?? false,
      completeness: coverageStats?.hasCompletenessCoverage ?? false,
      volume: coverageStats?.hasVolumeCoverage ?? false,
    })),
  )

  const hasDataAssets = assetData && assetData.length > 0
  const showTable = loading || hasDataAssets
  const showGettingStarted = !loading && !hasDataAssets && !error

  const dataSourceCount = data?.datasourcesV2?.length ?? 0
  const dataAssetCount = assetData?.length ?? 0

  return (
    <DataAssetsPageHeader loading={loading} dataAssetCount={dataAssetCount} dataSourceCount={dataSourceCount}>
      <MainContent>
        {error && (
          <MainContentBlock>
            <ErrorLoadingDataAssets />
          </MainContentBlock>
        )}
        {showGettingStarted && <DataAssetsEmptyScreen isEditorOrAdmin={isEditorOrAdmin} />}
        {hasDataAssets && !isDataHealthDashboardEnabled && (
          <MainContentBlock>
            <AggregateAssetCoverageStats />
          </MainContentBlock>
        )}
        {showTable && (
          <>
            {assetChangedMessage}
            <MainContentBlock size="large">
              <DataAssetsTable canEdit={isEditorOrAdmin} assetData={assetData} loading={loading} />
            </MainContentBlock>
          </>
        )}
      </MainContent>
    </DataAssetsPageHeader>
  )
}

type DataAssetsPageHeaderProps = {
  loading: boolean
  dataAssetCount: number
  dataSourceCount: number
  children: React.ReactNode
}
function DataAssetsPageHeader({ loading, dataAssetCount, dataSourceCount, children }: DataAssetsPageHeaderProps) {
  const canEdit = useRequireRole("EDITOR")
  return (
    <PageHeader
      headerContent={{
        title: "Data Assets",
        subtitle: `${dataSourceCount} ${pluralize("Data Source", dataSourceCount)}, ${dataAssetCount} ${pluralize(
          "Data Asset",
          dataAssetCount,
        )}`,
        rightActions: dataSourceCount
          ? {
              reactNode: canEdit && (
                <Space direction="horizontal">
                  <ManageDatasourcesButton />
                  <ConnectToDataButton />
                </Space>
              ),
            }
          : {},
      }}
      loading={loading}
    >
      {children}
    </PageHeader>
  )
}

function ErrorLoadingDataAssets() {
  return <ErrorState errorMessage="There was a problem loading Data Assets" />
}
